import { EmailAddress } from './EmailAddress';
import { PhoneNumber } from './PhoneNumber';
import { AddressBookEntry } from '../entities/AddressBookEntry';
import { AddressBookError } from '../errors/AddressBookError';

export interface RecipientData {
  contactId?: string;
  name: string;
  email?: string;
  phone?: string;
  viaEmail: boolean;
  viaSms: boolean;
}

export class Recipient {
  public readonly contactId?: string;
  public readonly name: string;
  public readonly email?: string;
  public readonly phone?: string;
  public readonly viaEmail: boolean;
  public readonly viaSms: boolean;

  constructor(data: RecipientData) {
    const trimmedName = data.name?.trim() ?? '';
    if (!trimmedName) {
      throw AddressBookError.entryNameRequired();
    }

    const normalizedEmail = data.email && data.email.trim()
      ? new EmailAddress(data.email.trim()).getValue()
      : undefined;
    const normalizedPhone = data.phone && data.phone.trim()
      ? new PhoneNumber(data.phone.trim()).getValue()
      : undefined;

    if (!normalizedEmail && !normalizedPhone) {
      throw AddressBookError.entryContactRequired();
    }

    if (data.viaEmail && !normalizedEmail) {
      throw AddressBookError.entryContactRequired();
    }
    if (data.viaSms && !normalizedPhone) {
      throw AddressBookError.entryContactRequired();
    }

    this.contactId = data.contactId;
    this.name = trimmedName;
    this.email = normalizedEmail;
    this.phone = normalizedPhone;
    this.viaEmail = data.viaEmail;
    this.viaSms = data.viaSms;
  }

  static fromAddressBookEntry(entry: AddressBookEntry, viaEmail: boolean, viaSms: boolean): Recipient {
    return new Recipient({
      contactId: entry.id,
      name: entry.name,
      email: entry.email,
      phone: entry.phone,
      viaEmail,
      viaSms
    });
  }

  static fromAdHoc(data: Omit<RecipientData, 'contactId'>): Recipient {
    return new Recipient(data);
  }

  toJSON(): RecipientData {
    return {
      contactId: this.contactId,
      name: this.name,
      email: this.email,
      phone: this.phone,
      viaEmail: this.viaEmail,
      viaSms: this.viaSms
    };
  }

  equals(other: Recipient): boolean {
    return this.contactId === other.contactId
      && this.name === other.name
      && this.email === other.email
      && this.phone === other.phone
      && this.viaEmail === other.viaEmail
      && this.viaSms === other.viaSms;
  }
}
